import { Button, GridColumn, GridRow, showToast, Table, TableColumns, Tooltip } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useConfirm } from '../../hooks/useConfirm';
import { useLoadBasicData } from '../../hooks/useLoadBasicData';
import {
  useDeleteSchoolEventLessonSyncMutation,
  useRetrySchoolEventLessonSyncsMutation,
  useSchoolEventLessonSyncsQuery,
} from '../../types/planung-graphql-client-defs';
import styles from './AdminEvents.module.scss';

type Footprint = {
  start: string;
  end: string;
  teacherUuids: string[];
  classUuids: string[];
  groupUuids: string[];
  roomUuids: string[];
};

type LessonSyncTableType = {
  uuid: string;
  created: string;
  range: string;
  teachers: string;
  classesOrGroups: string;
  attempts: number;
  lastError: string;
  lastAttempt: string;
};

const parseFootprints = (json: string): Footprint[] => {
  try {
    return JSON.parse(json) as Footprint[];
  } catch {
    return [];
  }
};

const unique = (values: string[]) => [...new Set(values)];

/**
 * Ausstehende Unterrichtsanpassungen nach dem Bearbeiten von Schulveranstaltungen. Bleibt hier ein
 * Eintrag stehen, konnte der Unterricht (noch) nicht angepasst werden – der Worker versucht es alle
 * 5 Minuten erneut, neue Änderungen an Schulveranstaltungen werden bis dahin abgelehnt.
 */
export const AdminSchoolEventLessonSyncs = observer(() => {
  const [{ data, fetching }, refetch] = useSchoolEventLessonSyncsQuery({ requestPolicy: 'network-only' });
  const [{ fetching: retrying }, retry] = useRetrySchoolEventLessonSyncsMutation();
  const [, deleteSync] = useDeleteSchoolEventLessonSyncMutation();
  const { ConfirmationDialog, confirm } = useConfirm();

  const { teacherData, classesData, groupsData } = useLoadBasicData({ pause: false });

  const handleRetry = async () => {
    const result = await retry({});
    const retryResult = result.data?.retrySchoolEventLessonSyncs;
    if (result.error || !retryResult) {
      showToast(`Retry failed: ${result.error?.message ?? 'unknown error'}`, { type: 'error' });
    } else if (retryResult.pending > 0) {
      showToast(`${retryResult.processed} processed, ${retryResult.pending} still pending`, { type: 'warning' });
    } else {
      showToast(`${retryResult.processed} processed`, { type: 'success' });
    }
    refetch();
  };

  const handleDelete = async (uuid: string) => {
    await confirm({
      title: 'Discard lesson sync',
      message:
        'The lessons affected by this change will NOT be adjusted (reset of freed lessons, removal of blocked teachers/rooms). Only discard if the lessons were fixed manually. Continue?',
      confirmText: 'Discard',
      cancelText: 'Cancel',
      onConfirm: async () => {
        const result = await deleteSync({ uuid });
        if (result.error) {
          showToast(`Discard failed: ${result.error.message}`, { type: 'error' });
        }
        refetch();
      },
    });
  };

  const tableData: LessonSyncTableType[] = useMemo(() => {
    const teacherName = (uuid: string) => teacherData?.people.find((t) => t.uuid === uuid)?.displayName ?? uuid;
    const classOrGroupName = (uuid: string) =>
      classesData?.classes.find((c) => c.uuid === uuid)?.name ??
      groupsData?.groups.find((g) => g.uuid === uuid)?.name ??
      uuid;

    return (
      data?.schoolEventLessonSyncs.map((sync) => {
        const footprints = [...parseFootprints(sync.oldFootprints), ...parseFootprints(sync.newFootprints)];
        const starts = footprints.map((f) => dayjs(f.start));
        const ends = footprints.map((f) => dayjs(f.end));
        const first = starts.reduce<dayjs.Dayjs | null>((min, d) => (!min || d.isBefore(min) ? d : min), null);
        const last = ends.reduce<dayjs.Dayjs | null>((max, d) => (!max || d.isAfter(max) ? d : max), null);

        return {
          uuid: sync.uuid,
          created: dayjs(sync.createdAt).format('DD.MM.YYYY HH:mm'),
          range: first && last ? `${first.format('DD.MM.YYYY HH:mm')} – ${last.format('DD.MM.YYYY HH:mm')}` : '',
          teachers: unique(footprints.flatMap((f) => f.teacherUuids))
            .map(teacherName)
            .join(', '),
          classesOrGroups: unique(footprints.flatMap((f) => [...f.classUuids, ...f.groupUuids]))
            .map(classOrGroupName)
            .join(', '),
          attempts: sync.attempts,
          lastError: sync.lastError ?? '',
          lastAttempt: sync.lastAttemptAt ? dayjs(sync.lastAttemptAt).format('DD.MM.YYYY HH:mm') : '',
        };
      }) ?? []
    );
  }, [classesData?.classes, data?.schoolEventLessonSyncs, groupsData?.groups, teacherData?.people]);

  const withTooltip = (value: string) => <Tooltip content={value}>{value}</Tooltip>;

  const columns: TableColumns<LessonSyncTableType>[] = [
    {
      id: 'state',
      header: 'Status',
      size: 110,
      accessorFn: ({ attempts }) => (attempts > 0 ? 'FAILED' : 'PENDING'),
      cell: ({ row }) =>
        row.original.attempts > 0 ? (
          <span className={`${styles.status} ${styles.error}`}>FAILED</span>
        ) : (
          <span className={`${styles.status} ${styles.warning}`}>PENDING</span>
        ),
    },
    { id: 'created', accessorKey: 'created', header: 'Created', size: 150 },
    { id: 'range', accessorKey: 'range', header: 'Affected period', size: 300 },
    {
      id: 'classesOrGroups',
      accessorKey: 'classesOrGroups',
      header: 'Classes / Groups',
      cell: ({ cell }) => withTooltip(cell.getValue() as string),
    },
    {
      id: 'teachers',
      accessorKey: 'teachers',
      header: 'Teachers',
      cell: ({ cell }) => withTooltip(cell.getValue() as string),
    },
    {
      id: 'lastError',
      accessorKey: 'lastError',
      header: 'Last error',
      cell: ({ row }) =>
        row.original.lastError ? (
          <Tooltip
            content={`${row.original.attempts}× failed, last ${row.original.lastAttempt}: ${row.original.lastError}`}
          >
            <span className={styles.error}>{row.original.lastError}</span>
          </Tooltip>
        ) : (
          <span className={styles.muted}>–</span>
        ),
    },
    {
      id: 'uuid',
      accessorKey: 'uuid',
      header: 'UUID',
      size: 100,
      cell: ({ cell }) => (
        <Tooltip content={cell.getValue() as string}>
          <span className={styles.mono}>{(cell.getValue() as string).slice(0, 8)}</span>
        </Tooltip>
      ),
    },
  ];

  const pendingCount = tableData.length;

  return (
    <>
      <GridRow
        headline={pendingCount > 0 ? `School event lesson syncs (${pendingCount})` : 'School event lesson syncs'}
        tooltip={
          'Lesson adjustments after editing school events. An entry only remains here if the lessons could not be adjusted yet. The worker retries every 5 minutes; until then, further edits of school events are rejected.'
        }
      >
        <GridColumn width={12}>
          <Table
            loading={fetching}
            columns={columns}
            data={tableData}
            showPagination={pendingCount > 5}
            pageSize={5}
            emptyStateSettings={{
              size: 'small',
              hideIcon: true,
              title: 'All school event changes have been applied to the lessons',
              subtitle: '',
            }}
            showActionBar={pendingCount > 0}
            actionBarSettings={{
              showGlobalFilter: false,
              extendedActionsRight: (
                <Button onClick={handleRetry} hierarchy={'primary'} disabled={retrying}>
                  Retry now
                </Button>
              ),
            }}
            lastColWidth='110px'
            lastCol={({ original }) => (
              <Button hierarchy='secondary' onClick={() => handleDelete(original.uuid)}>
                Discard
              </Button>
            )}
          />
        </GridColumn>
      </GridRow>
      <ConfirmationDialog />
    </>
  );
});
